import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  FileText,
  Briefcase,
  Sparkles,
  Users,
  Loader2,
  AlertTriangle,
  Hash,
  ChevronDown,
  Save,
  X,
} from "lucide-react";
import {
  useGetAdminProjectDetails,
  useUpdateProject,
} from "../../lib/api/adminProjects";

// Components
import ProjectFormBasicInfo from "../../components/admin/projects/ProjectFormBasicInfo";
import ProjectFormDetails from "../../components/admin/projects/ProjectFormDetails";
import ProjectFormResponsibilities from "../../components/admin/projects/ProjectFormResponsibilities";
import ProjectFormSkills from "../../components/admin/projects/ProjectFormSkills";
import ProjectFormSidebar from "../../components/admin/projects/ProjectFormSidebar";

// Zod Schema
const responsibilitySchema = z.object({
  order: z.number().int().min(1),
  description: z
    .string()
    .min(5, "Responsibility must be at least 5 characters")
    .max(500, "Responsibility cannot exceed 500 characters"),
});

const skillSchema = z.object({
  name: z
    .string()
    .min(2, "Skill name must be at least 2 characters")
    .max(50, "Skill name cannot exceed 50 characters"),
  required: z.boolean().default(true),
});

const projectSchema = z.object({
  title: z
    .string()
    .min(5, "Project title must be at least 5 characters")
    .max(100, "Project title cannot exceed 100 characters"),
  category: z.string().min(1, "Category is required"),
  description: z
    .string()
    .min(20, "Description must be at least 20 characters")
    .max(2000, "Description cannot exceed 2000 characters"),
  hourlyRate: z
    .number()
    .min(1, "Hourly rate must be at least $1")
    .max(500, "Hourly rate cannot exceed $500"),
  estimatedHours: z.string().min(1, "Estimated hours is required"),
  difficulty: z.string().min(1, "Difficulty is required"),
  totalSlots: z
    .number()
    .int("Total slots must be a whole number")
    .min(1, "Total slots must be at least 1")
    .max(1000, "Total slots cannot exceed 1000"),
  responsibilities: z
    .array(responsibilitySchema)
    .min(1, "At least one responsibility is required"),
  skillsRequired: z.array(skillSchema).min(1, "At least one skill is required"),
});

export type ProjectFormData = z.infer<typeof projectSchema>;

const sections = [
  { id: "basic", label: "Basic Info", icon: FileText },
  { id: "details", label: "Details", icon: Briefcase },
  { id: "responsibilities", label: "Responsibilities", icon: Sparkles },
  { id: "skills", label: "Skills", icon: Users },
];

const EditProject: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const {
    data: projectData,
    isLoading,
    isError,
  } = useGetAdminProjectDetails(projectId!);
  const { mutateAsync: updateProject, isPending } = useUpdateProject();

  const [activeSection, setActiveSection] = useState<string>("basic");
  const [hasChanges, setHasChanges] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);

  const project = projectData?.data;

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema) as any,
    defaultValues: {
      title: "",
      category: "",
      description: "",
      hourlyRate: undefined,
      estimatedHours: "",
      difficulty: "",
      totalSlots: undefined,
      responsibilities: [{ order: 1, description: "" }],
      skillsRequired: [{ name: "", required: true }],
    },
    mode: "onChange",
  });

  const responsibilitiesFieldArray = useFieldArray({
    control,
    name: "responsibilities",
  });

  const skillsFieldArray = useFieldArray({
    control,
    name: "skillsRequired",
  });

  const watchedValues = watch();

  // Populate form with project data when loaded
  useEffect(() => {
    if (project) {
      reset({
        title: project.title,
        category: project.category,
        description: project.description,
        hourlyRate: project.hourlyRate,
        estimatedHours: project.estimatedHours,
        difficulty: project.difficulty,
        totalSlots: project.totalSlots,
        responsibilities: project.responsibilities?.length
          ? project.responsibilities.map((r: any, index: number) => ({
              order: r.order || index + 1,
              description: r.description,
            }))
          : [{ order: 1, description: "" }],
        skillsRequired: project.skillsRequired?.length
          ? project.skillsRequired.map((s: any) => ({
              name: s.name,
              required: s.required ?? true,
            }))
          : [{ name: "", required: true }],
      });
    }
  }, [project, reset]);

  // Track changes
  useEffect(() => {
    setHasChanges(isDirty);
  }, [isDirty]);

  const onSubmit = async (data: ProjectFormData) => {
    try {
      const formattedData = {
        ...data,
        responsibilities: data.responsibilities.map((r, index) => ({
          ...r,
          order: index + 1,
        })),
      };

      await updateProject({
        projectId: projectId!,
        payload: formattedData,
      });

      navigate("/admin/projects");
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  const toggleSection = (sectionId: string) => {
    setActiveSection(activeSection === sectionId ? "" : sectionId);
    setShowMobileNav(false);
  };

  const handleMobileNavSelect = (sectionId: string) => {
    setActiveSection(sectionId);
    setShowMobileNav(false);
    // Scroll to section
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF] mx-auto mb-4" />
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Project Not Found
          </h2>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            The project you're looking for doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate("/admin/projects")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate("/admin/projects")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 transition-colors text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden xs:inline">Back to Projects</span>
          <span className="xs:hidden">Back</span>
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
              Edit Project
            </h1>
            <p className="text-gray-500 mt-1 text-xs sm:text-sm hidden sm:block">
              Update project details. Changes will be saved when you click "Save
              Changes".
            </p>
          </div>
          {hasChanges && (
            <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto">
              <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-amber-500 rounded-full animate-pulse" />
              Unsaved changes
            </span>
          )}
        </div>
      </div>

      {/* Project Info Banner */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 lg:p-5 bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Top row - Icon, Title, and Status */}
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Editing Project</p>
                <h2 className="font-semibold text-gray-900 text-sm sm:text-base truncate max-w-[180px] xs:max-w-[220px] sm:max-w-[300px] lg:max-w-none">
                  {project.title}
                </h2>
              </div>
            </div>

            {/* Status badge - Always visible */}
            <span
              className={`inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                project.isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  project.isActive ? "bg-emerald-500" : "bg-gray-400"
                }`}
              />
              <span className="hidden xs:inline">
                {project.isActive ? "Active" : "Inactive"}
              </span>
            </span>
          </div>

          {/* Bottom row - Meta info */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            {/* Project ID */}
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg">
              <Hash className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400" />
              <span className="font-mono text-xs truncate max-w-[100px] sm:max-w-none">
                {project.projectId}
              </span>
            </span>

            {/* Slots info */}
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg">
              <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400" />
              <span>
                {project.filledSlots}/{project.totalSlots} slots
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Section Navigation */}
      <div className="lg:hidden mb-4">
        <div className="relative">
          <button
            onClick={() => setShowMobileNav(!showMobileNav)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700"
          >
            <div className="flex items-center gap-2">
              {(() => {
                const currentSection = sections.find(
                  (s) => s.id === activeSection
                );
                const Icon = currentSection?.icon || FileText;
                return (
                  <>
                    <Icon className="w-4 h-4 text-[#8B52FF]" />
                    <span>{currentSection?.label || "Select Section"}</span>
                  </>
                );
              })()}
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                showMobileNav ? "rotate-180" : ""
              }`}
            />
          </button>

          {showMobileNav && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMobileNav(false)}
              />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleMobileNavSelect(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                        isActive
                          ? "bg-[#8B52FF]/5 text-[#8B52FF]"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${isActive ? "text-[#8B52FF]" : "text-gray-400"}`}
                      />
                      <span className={isActive ? "font-medium" : ""}>
                        {section.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div id="section-basic">
              <ProjectFormBasicInfo
                register={register}
                control={control}
                errors={errors}
                isActive={activeSection === "basic"}
                onToggle={() => toggleSection("basic")}
                descriptionLength={watchedValues.description?.length || 0}
              />
            </div>

            <div id="section-details">
              <ProjectFormDetails
                register={register}
                control={control}
                errors={errors}
                isActive={activeSection === "details"}
                onToggle={() => toggleSection("details")}
              />
            </div>

            <div id="section-responsibilities">
              <ProjectFormResponsibilities
                register={register}
                control={control}
                errors={errors}
                fieldArray={responsibilitiesFieldArray}
                isActive={activeSection === "responsibilities"}
                onToggle={() => toggleSection("responsibilities")}
              />
            </div>

            <div id="section-skills">
              <ProjectFormSkills
                register={register}
                control={control}
                errors={errors}
                fieldArray={skillsFieldArray}
                isActive={activeSection === "skills"}
                onToggle={() => toggleSection("skills")}
              />
            </div>
          </div>

          {/* Sidebar - Hidden on mobile */}
          <div className="hidden lg:block">
            <ProjectFormSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionClick={setActiveSection}
              watchedValues={watchedValues}
              isValid={isValid}
              isPending={isPending}
              isEditMode={true}
              hasChanges={hasChanges}
            />
          </div>
        </div>

        {/* Mobile Fixed Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 p-4 z-30">
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <button
              type="button"
              onClick={() => navigate("/admin/projects")}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !isValid || !hasChanges}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditProject;
